import React, { useEffect } from "react";
import { isCoarsePointer } from "../../hooks/useViewport";
import "./SlidePanel.css";

/**
 * SlidePanel — right-side drawer that replaces Bootstrap modals.
 * Props:
 *   show     {boolean}   — whether the panel is visible
 *   onHide   {function}  — called when backdrop or ✕ is clicked
 *   title    {string}    — panel header text
 *   children {ReactNode} — scrollable body content
 *   footer   {ReactNode} — optional sticky footer (Submit/Cancel buttons)
 *   width    {number}    — desktop width in px (default 480)
 */
function SlidePanel({ show, onHide, title, children, footer, width = 480 }) {
  const closeSize = isCoarsePointer() ? 44 : 32;
  // Lock body scroll while open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [show]);

  // Close on Escape key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && show) onHide();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show, onHide]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onHide}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 1054,
          opacity: show ? 1 : 0,
          pointerEvents: show ? "auto" : "none",
          transition: "opacity 0.25s ease",
        }}
      />

      {/* Drawer panel */}
      <div
        className="app-slide-panel"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          // dvh, not vh: on iPad Safari 100vh is the *largest* viewport, so the sticky
          // footer (Save / Cancel) slid under the toolbar while it was showing.
          height: "100dvh",
          width: "min(100vw, " + width + "px)",
          background: "#fff",
          zIndex: 1055,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-4px 0 32px rgba(0,0,0,0.13)",
          transform: show ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px",
            borderBottom: "1px solid #e5e7eb",
            flexShrink: 0,
          }}
        >
          <h5
            style={{
              margin: 0,
              fontSize: "16px",
              fontWeight: 700,
              color: "#111827",
            }}
          >
            {title}
          </h5>
          <button
            onClick={onHide}
            aria-label="Close"
            style={{
              background: "none",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              width: `${closeSize}px`,
              height: `${closeSize}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              fontSize: "18px",
              color: "#6b7280",
              lineHeight: 1,
              flexShrink: 0,
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#fdf2f5";
              e.currentTarget.style.borderColor = "#C2185B";
              e.currentTarget.style.color = "#C2185B";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "none";
              e.currentTarget.style.borderColor = "#e5e7eb";
              e.currentTarget.style.color = "#6b7280";
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "24px",
          }}
        >
          {children}
        </div>

        {/* Footer (optional) */}
        {footer && (
          <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid #e5e7eb",
              flexShrink: 0,
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
              background: "#fff",
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </>
  );
}

export default SlidePanel;
